import { useMemo, useState } from 'react';
import {
  ChevronRight, Loader2, Mail, MoreVertical, Search, UserCog, UserPlus, Users,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import type { UserProfile } from '../../lib/api';

interface GroupUsersCardProps {
  users: UserProfile[];
  groupId: number;
  canImpersonate: boolean;
  impersonating: number | null;
  isSelf: (userProfile: { user: { id: number } }) => boolean;
  onAddUser: () => void;
  onOpenUser: (user: UserProfile) => void;
  onImpersonate: (user: UserProfile) => void;
}

function userDisplayName(u: UserProfile): string {
  if (u.user.first_name || u.user.last_name) {
    return `${u.user.first_name ?? ''} ${u.user.last_name ?? ''}`.trim();
  }
  return u.user.email;
}

function userInitials(u: UserProfile): string {
  return ((u.user.first_name?.[0] ?? '') + (u.user.last_name?.[0] ?? '')).toUpperCase() || '??';
}

/**
 * Searchable group-level user list with invite CTA and admin actions.
 */
export function GroupUsersCard({
  users,
  groupId: _groupId,
  canImpersonate,
  impersonating,
  isSelf,
  onAddUser,
  onOpenUser,
  onImpersonate,
}: GroupUsersCardProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const haystack = [
        u.user.email,
        u.user.first_name,
        u.user.last_name,
        u.company_name,
        u.role_name,
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [users, search]);

  return (
    <Card className="h-full">
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Users
            </CardTitle>
            <CardDescription>
              {users.length} {users.length === 1 ? 'user' : 'users'} with access across member companies
            </CardDescription>
          </div>
          <Button size="sm" onClick={onAddUser}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
        {users.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="pl-9"
              aria-label="Search group users"
            />
          </div>
        )}
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium">No users yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Invite someone to give them access to companies in this group.
            </p>
            <Button className="mt-4" onClick={onAddUser}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No users match &quot;{search}&quot;.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u) => (
              <div
                key={u.id}
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onOpenUser(u)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenUser(u);
                  }
                }}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-sm font-semibold text-purple-600 dark:bg-purple-950 dark:text-purple-300">
                    {userInitials(u)}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-sm">{userDisplayName(u)}</p>
                    <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                      <Mail className="h-3 w-3 shrink-0" />
                      {u.user.email}
                      {u.company_name && (
                        <>
                          <span className="opacity-50">·</span>
                          {u.company_name}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {u.role_name && (
                    <Badge variant="outline" className="hidden sm:inline-flex text-xs">{u.role_name}</Badge>
                  )}
                  <Badge className={u.is_approved
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }>
                    {u.is_approved ? 'Active' : 'Pending'}
                  </Badge>
                  {canImpersonate && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="User actions">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenUser(u); }}>
                          <Users className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); onImpersonate(u); }}
                          disabled={isSelf(u) || !u.user.is_active || impersonating === u.user.id}
                        >
                          {impersonating === u.user.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <UserCog className="h-4 w-4 mr-2" />
                          )}
                          Login As
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
